import { useCallback, useEffect, useState, type ReactNode } from "react";
import { t } from "i18next";
import { DangerConfirmModal } from "@components/DangerConfirmModal";

export interface UseUnsavedExitConfirmOptions {
  /** 主 Modal 是否打开（用于重置 confirm 状态） */
  open: boolean;
  /** 真正关闭主 Modal */
  onClose: () => void;
  isDirty: boolean;
  /** 是否启用拦截，默认 true */
  shouldGuard?: boolean;
  /** 阻塞关闭，如 submitting */
  blocked?: boolean;
  copy?: {
    title: ReactNode;
    description: ReactNode;
    confirmText: ReactNode;
    cancelText?: ReactNode;
  };
}

const DEFAULT_COPY = {
  title: () => t("Exit without saving?"),
  description: () => t("Your changes won't be saved. Continue?"),
  confirmText: () => t("Exit"),
  cancelText: () => t("Cancel"),
};

export const useUnsavedExitConfirm = ({
  open,
  onClose,
  isDirty,
  shouldGuard = true,
  blocked = false,
  copy,
}: UseUnsavedExitConfirmOptions) => {
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (!open) {
      setConfirmOpen(false);
    }
  }, [open]);

  const requestClose = useCallback(() => {
    if (blocked) return;
    if (shouldGuard && isDirty) {
      setConfirmOpen(true);
      return;
    }
    onClose();
  }, [blocked, shouldGuard, isDirty, onClose]);

  const handleConfirmExit = useCallback(() => {
    setConfirmOpen(false);
    onClose();
  }, [onClose]);

  const handleStay = useCallback(() => {
    setConfirmOpen(false);
  }, []);

  const resolvedCopy = {
    title: copy?.title ?? DEFAULT_COPY.title(),
    description: copy?.description ?? DEFAULT_COPY.description(),
    confirmText: copy?.confirmText ?? DEFAULT_COPY.confirmText(),
    cancelText: copy?.cancelText ?? DEFAULT_COPY.cancelText(),
  };

  const exitConfirmModal = (
    <DangerConfirmModal
      open={confirmOpen}
      title={resolvedCopy.title}
      description={resolvedCopy.description}
      confirmText={resolvedCopy.confirmText}
      cancelText={resolvedCopy.cancelText}
      onConfirm={handleConfirmExit}
      onCancel={handleStay}
    />
  );

  return { requestClose, exitConfirmModal };
};
